import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    BooleanInput,
    FormWithRedirect,
    SaveButton,
    TextInput,
    Toolbar,
    TranslatableInputs,
    useNotify,
    useRefresh,
} from 'react-admin';
import { useHistory } from 'react-router-dom';
import { Typography } from '@material-ui/core';
import { CircularProgress } from '@material-ui/core';

import Title from '../../components/list/Title';
import cfgLocales from '../../config/locale';
import axiosForApi from '../../config/axios-for-api';
import { handleError } from '../../functions/common';
import { listEditCreate } from '../../styles/listEditCreate';

type ProductReviewReplyProps = {
    id: string | undefined;
    basePath?: string;
    resource?: string;
};

type ReplyRecord = {
    id?: string;
    shop_reply_translations?: Record<string, string>;
    is_shop_reply_published?: boolean;
};

const normalizeReply = (data: unknown): ReplyRecord => {
    if (data && typeof data === 'object' && 'shop_reply_translations' in data) {
        return data as ReplyRecord;
    }
    if (data && typeof data === 'object') {
        const anyData = data as Record<string, unknown>;
        if ('reply_translations' in anyData && typeof anyData.reply_translations === 'object') {
            return { shop_reply_translations: anyData.reply_translations as Record<string, string> };
        }
    }
    return { shop_reply_translations: {} };
};

const ProductReviewReply = (props: ProductReviewReplyProps) => {
    const { id, basePath = '', resource = 'admin/product-reviews' } = props;
    const [reply, setReply] = useState<ReplyRecord | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const notify = useNotify();
    const refresh = useRefresh();
    const history = useHistory();
    const classes = listEditCreate();

    useEffect(() => {
        if (!id) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        axiosForApi
            .get(`api/admin/product-reviews/${id}/reply`)
            .then(({ data }) => {
                if (!cancelled) {
                    setReply(normalizeReply(data));
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(handleError(e));
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [id]);

    const handleSave = async (values: Partial<Record<string, unknown>>) => {
        if (!id) return;
        try {
            await axiosForApi.put(`api/admin/product-reviews/${id}/reply`, values);
            notify('ra.notification.saved', 'success');
            refresh();
            history.push(basePath);
        } catch (e) {
            notify(handleError(e), 'error');
            throw e;
        }
    };

    if (loading) {
        return (
            <div className={classes.sidebar} style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return (
            <div className={classes.sidebar} style={{ padding: '2rem' }}>
                <Typography color="error">{error}</Typography>
            </div>
        );
    }

    if (!id) {
        return null;
    }

    const record: ReplyRecord & { id: string } = {
        id,
        ...(reply ?? { shop_reply_translations: {} }),
    };

    return (
        <FormWithRedirect
            record={record}
            resource={resource}
            basePath={basePath}
            save={handleSave}
            redirect={false}
            render={(formProps: any) => (
                <div className={classes.sidebar}>
                    <Title
                        record={{ id }}
                        source="id"
                        label="resources.admin/product-reviews.titles.reply"
                    />
                    <TranslatableInputs
                        locales={Object.keys(cfgLocales.languageOptions)}
                        defaultLocale={cfgLocales.defaultLocale}
                    >
                        <TextInput
                            source="shop_reply_translations"
                            label="resources.admin/product-reviews.fields.shop_reply_translations"
                            fullWidth
                            multiline
                            rows={6}
                        />
                    </TranslatableInputs>
                    <BooleanInput
                        source="is_shop_reply_published"
                        label="resources.admin/product-reviews.fields.is_shop_reply_published"
                    />
                    <Toolbar>
                        <SaveButton
                            saving={formProps.saving}
                            handleSubmitWithRedirect={formProps.handleSubmitWithRedirect ?? (() => formProps.handleSubmit())}
                        />
                    </Toolbar>
                </div>
            )}
        />
    );
};

export default ProductReviewReply;
